import {
  ContentValue,
  Localized,
  RichtextValue,
  XcoreCore,
  resolveLocalizedValue,
  LocalesValue,
  XcoreFiles,
  FileValue,
  Localizable,
  XCImageSize,
  FileOptions,
} from "@robe/core";
import { isRichtextPlainText } from "../../utils";
import { Node } from "slate";

export interface LocalizationFuncOptions {
  fallbackToAnotherLocale?: boolean;
  localeOverride?: string;
}

export type LocalizeFuncValue<T extends ContentValue> = T | Localizable<T> | undefined;

export interface LocalizeFunc {
  <T extends ContentValue>(val: LocalizeFuncValue<T>, options?: LocalizationFuncOptions): T | null;
}

export type StringifyFuncValue<T extends string> = T | Localizable<null | T> | undefined | null;

export interface StringifyFunc {
  <T extends string = string>(val: StringifyFuncValue<T>, options?: LocalizationFuncOptions): T | "";
}

export interface FormatDateFunc {
  (date: string | Date | Localizable<string | Date>): string | null;
}

export interface VideoFunc {
  (videoId: number): string;
}

export interface ImageFunc {
  <TSize extends XCImageSize>(fileValue: FileValue<TSize>, options?: FileOptions<TSize>): string | null;
}

type PossibleRichtextValueTypesUnion = RichtextValue | Localized<RichtextValue>;

export interface RichtextToStringFunc {
  (richtext: PossibleRichtextValueTypesUnion, newline?: string): string;
  (richtext: RichtextInput, newline?: string): string | undefined;
}

export interface XcoreDataUtils {
  localize: LocalizeFunc;
  stringify: StringifyFunc;
  formatDate: FormatDateFunc;
  image: ImageFunc;
  video: VideoFunc;
  richtextToString: RichtextToStringFunc;
}

export const xcoreDataUtils = ({ locales, currentLocale, files }: XcoreCore): XcoreDataUtils => ({
  localize: localize({ locales, currentLocale }),
  stringify: stringify({ locales, currentLocale }),
  formatDate: formatDate({ locales, currentLocale }),
  image: imageFunc(files),
  video: videoFunc(files),
  richtextToString: richtextToString({ locales, currentLocale }) as RichtextToStringFunc,
});

interface XcoreDataUtilsInternalProps {
  locales: Record<string, LocalesValue>;
  currentLocale: string;
}

const imageFunc =
  (files: XcoreFiles) =>
  <TSize extends XCImageSize>(fileValue: FileValue<TSize>, options?: FileOptions<TSize>) =>
      files.getImageUrl<TSize>(fileValue, options);

const videoFunc = (files: XcoreFiles) => (videoId: number) => files.getHostedVideoUrl(videoId);

// When working with all locales specified in the project, this function must be ALWAYS used instead of native toLocaleDateString
const toRobeLocaleDateString = (date: Date, locale: string) => {
  const localeResolved =
    locale ===
    "en-SEA" /* en-SEA is not part of BCP 47 language tag standard, hence toLocaleDateString("en-SEA") throws an error. Using Singaporean date format instead  */
      ? "en-SG"
      : locale === "en" /* On global EN locale European date format is used defaultly in a format of DD.MM.YYYY */
        ? "de-DE"
        : locale;
  return date.toLocaleDateString(localeResolved);
};

const formatDate = ({ locales, currentLocale }: XcoreDataUtilsInternalProps) => {
  return (value: string | Date | Localized<string | Date>) => {
    let resolvedLocalizedValue: Date | string = value as Date | string;
    let resolvedLocale: string | null = null;

    if (typeof value === "object" && Object.keys(value).some((v) => Object.keys(locales).includes(v))) {
      /**
       * If value is localized resolve its value and locale.
       * Resolved locale can be fallback locale so it is being returned too
       * so it is sure the correct locale is being used for date string conversion.
       */
      const resolvedLocalizedValueNullable = resolveLocalizedValue(currentLocale, locales)(value as Localized<Date | string>);

      if (!resolvedLocalizedValueNullable) {
        return null;
      }

      resolvedLocalizedValue = resolvedLocalizedValueNullable.value;
      resolvedLocale = resolvedLocalizedValueNullable.locale;
    }

    /** If provided value was `string` create new `Date` from it */
    const dateValueResolved = typeof resolvedLocalizedValue === "string" ? new Date(resolvedLocalizedValue) : resolvedLocalizedValue;

    // If value wasn't localized `resolvedLocale` will be `null`
    return dateValueResolved ? toRobeLocaleDateString(dateValueResolved, resolvedLocale ?? currentLocale) : null;
  };
};

export const nodeToString = (node: Node): string => {
  if (isRichtextPlainText(node)) {
    return (node as any).text;
  }

  return (node as any).children.map(nodeToString).join("");
};

const isValidRichtextValue = (x: any): x is RichtextValue => x && typeof x === "object" && "value" in x && Array.isArray(x.value);

type RichtextInput = RichtextValue | Localized<RichtextValue> | undefined;

const richtextToString =
  (internalProps: XcoreDataUtilsInternalProps) =>
    (input: RichtextInput): string | undefined => {
      if (input === undefined) {
        return undefined;
      }

      let richtextValue = input as RichtextValue;

      // If input is not a valid richtext value, it is likely that it is localized richtext value.
      if (!isValidRichtextValue(input)) {
        const localizedRichtextValueResolved = localize(internalProps)(input);
        // If it still isn't a valid richtext value return undefined.
        if (!isValidRichtextValue(localizedRichtextValueResolved)) {
          return undefined;
        }

        richtextValue = localizedRichtextValueResolved;
      }

      return richtextValue?.value.map(nodeToString).join("\n");
    };

const localize =
  ({ locales, currentLocale }: XcoreDataUtilsInternalProps) =>
  <T extends ContentValue>(value: LocalizeFuncValue<T>, options?: LocalizationFuncOptions): T | null => {
    if (!value) {
      return null;
    }

    // Non localized value of localizable field
    if (typeof value === "object" && "value" in value) {
      return value.value as T;
    }

    return (
      resolveLocalizedValue(options?.localeOverride ?? currentLocale, locales)(value as Localized<T>, options?.fallbackToAnotherLocale)
        ?.value ?? null
    );
  };

/**
 * Works just like `localize()` only difference is
 * that it accepts only `string` or localized `string` and returns empty `string` instead of `undefined`
 * */
const stringify =
  <T extends string>(internalProps: XcoreDataUtilsInternalProps) =>
    (value: StringifyFuncValue<T>, options?: LocalizationFuncOptions): T | "" => {
      if (!value) {
        return "";
      }

      if (typeof value === "object" && Object.values(value).every((v) => v === null)) {
        return "";
      }

      if (typeof value === "string") {
        return value;
      }

      const result = localize(internalProps)(value, options);

      if (!result) {
        return "";
      }

      if (typeof result !== "string") {
        console.warn("Invalid stringify input:", { input: value, result });
        return "";
      }

      return result;
    };
